import { difference, uniq } from 'lodash';
import { IssuerType, Region, Tag } from '../models';

export const COMPANY_TAG_PREFIX = 'company/';
export const MUNICIPAL_TAG_PREFIX = 'municipal/';
export const REPORT_SOURCE_TAG_PREFIX = 'report-source/';
export const MULTIPART_TAG_PREFIX = 'multi_part/';

const TYPE_TAG_PREFIX = 'type-tag/';
const FEED_TAG_PREFIX = 'feedtag/';
const PUBLISHER_PREFIX = 'publisher/';
const ANALYST_PREFIX = 'analyst/';

const EURO_MORNING_COMMENT = 'type-tag/euro_morning_comment';
const US_MORNING_COMMENT = 'type-tag/us_morning_comment';

export const BULLET_COMPONENT = 'component/bullets';
export const CR_DOCUMENT_REPORT_COMPONENT = 'component/cr_document_report';
export const SECTOR_REVIEW = 'sector_review';

export const isAnalystTag = (tag: string | undefined): boolean => !!tag?.startsWith(ANALYST_PREFIX);
export const isCompanyTag = (tag: string | undefined): boolean => !!tag?.startsWith(COMPANY_TAG_PREFIX);
export const isMunicipalTag = (tag: string | undefined): boolean => !!tag?.startsWith(MUNICIPAL_TAG_PREFIX);
export const isReportSourceTag = (tag: string | undefined): boolean => !!tag?.startsWith(REPORT_SOURCE_TAG_PREFIX);
export const isMultipartTag = (tag: string | undefined): boolean => !!tag?.startsWith(MULTIPART_TAG_PREFIX);

export const getCompanies = (tags: Tag[] = []): Tag[] => {
  // TODO: Add ticker filtering when Ticker data is fixed
  // return tags.filter((tag) => isCompanyTag(tag.tagId) && tag.ticker);
  return tags.filter((tag) => isCompanyTag(tag.tagId));
};

export const getIssuerTag = (id: string, issuerType: IssuerType): string => {
  switch (issuerType) {
    case IssuerType.Company:
      return getCompanyTag(id);
    case IssuerType.Municipal:
      return getMunicipalTag(id);
    default:
      return '';
  }
};

export const getCompanyTag = (id: string): string => {
  return `${COMPANY_TAG_PREFIX}${id}`;
};

export const getMunicipalTag = (id: string): string => {
  return `${MUNICIPAL_TAG_PREFIX}${id}`;
};

export const extractIssuerIds = (tags: Tag[]): string[] => {
  return tags.reduce((acc, tag) => {
    return isCompanyTag(tag.tagId) || isMunicipalTag(tag.tagId) ? [...acc, tag.tagId] : acc;
  }, [] as string[]);
};
export const extractMultipartTag = (tags: Tag[]): string | undefined =>
  tags.find((tag) => isMultipartTag(tag.tagId))?.tagId;
export const getCompanyId = (companyTagId: string): string => companyTagId.replace(COMPANY_TAG_PREFIX, '');
export const getAnalystId = (analystTagId: string): string => analystTagId.replace(ANALYST_PREFIX, '');
export const getRelatedTopics = (tags: Tag[] = [], focusTags: Tag[] = []): Tag[] => {
  const allTags = uniq([...tags, ...focusTags]);
  const excludedPrefixes = [
    COMPANY_TAG_PREFIX,
    MUNICIPAL_TAG_PREFIX,
    TYPE_TAG_PREFIX,
    FEED_TAG_PREFIX,
    PUBLISHER_PREFIX,
    ANALYST_PREFIX,
    REPORT_SOURCE_TAG_PREFIX,
  ];

  return allTags.filter(({ tagId }) => {
    const prefix = tagId.substring(0, tagId.indexOf('/') + 1);
    return !excludedPrefixes.includes(prefix);
  });
};

export const getSecondaryCompanyIds = (focusCompanyIds: string[], companyIds: string[]): string[] => {
  return difference(companyIds, focusCompanyIds);
};

export const getMorningCommentRegion = (tags: Tag[] = []): Region | null => {
  const morningCommentTag = tags.find((tag) => tag.tagId === EURO_MORNING_COMMENT || tag.tagId === US_MORNING_COMMENT);
  switch (morningCommentTag?.tagId) {
    case EURO_MORNING_COMMENT:
      return Region.Europe;
    case US_MORNING_COMMENT:
      return Region.UnitedStates;
    default:
      return null;
  }
};

export const isBulletComponent = (tags: Tag[]): boolean => tags.some((tag) => tag.tagId === BULLET_COMPONENT);
export const containsSectorReviewTag = (tags: Tag[]): boolean => tags.some((tag) => tag.tagId.endsWith(SECTOR_REVIEW));
